import { useApp } from '@/contexts';

import { useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import {
  useGetWorkspaceUsersQuery,
  useRemoveWorkspaceUserMutation,
  useUpdateWorkspaceUserAdminMutation,
} from '@features/apps';
import { useGetUsersQuery, useInviteUsersToProjectMutation } from '@features/users';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { DeleteConfirmDialog } from '@/components/common';
import { PageContainer, PageHeading } from '@/components/layout';
import type { UserPermission } from '@/types';

/**
 * Get tooltip text for role toggle
 */
const getRoleTooltipText = (
  isSelf: boolean,
  isUpdatingRole: boolean,
  isCurrentUserAdmin: boolean
): string => {
  if (!isCurrentUserAdmin) return 'Only admins can change user roles';
  if (isSelf) return 'You cannot change your own role';
  if (isUpdatingRole) return 'Updating role...';
  return '';
};

/**
 * Workspace team management page
 * Matches dataProtect's userPicker.js design exactly
 */
export const TeamPage = () => {
  const { appId, tenantId } = useApp();
  const { user: currentUser } = useAuth0();
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{ userId: string; user: UserPermission } | null>(
    null
  );
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    data: users,
    isLoading: isLoadingUsers,
    error,
  } = useGetWorkspaceUsersQuery({ tenantId, appId }, { skip: !tenantId || !appId });

  const [removeUser] = useRemoveWorkspaceUserMutation();
  const [updateUserAdmin] = useUpdateWorkspaceUserAdminMutation();

  const isLoading = isLoadingUsers;

  // Sort users alphabetically by display name
  // Note: Users without valid Identity are already filtered out by the mapper
  const sortedUsers = useMemo(() => {
    if (!users) return [];

    return [...users].sort((a, b) => {
      const nameA = (a.username || a.email || '').toLowerCase();
      const nameB = (b.username || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  // Check if a user is the current logged-in user
  const isCurrentUser = (user: UserPermission) => {
    if (!currentUser?.email) return false;

    // Check multiple possible email fields from different API response structures
    const userEmail = user.email || user.username || '';

    return userEmail.toLowerCase() === currentUser.email.toLowerCase();
  };

  // Find the current user's permission record to check if they're an admin
  const currentUserPermission = useMemo(() => {
    if (!users || !currentUser?.email) return null;
    const currentEmail = currentUser.email.toLowerCase();
    return users.find((u) => {
      const userEmail = u.email || u.username || '';
      return userEmail.toLowerCase() === currentEmail;
    });
  }, [users, currentUser?.email]);

  // Check if current user is an admin
  const isCurrentUserAdmin = currentUserPermission?.isAdmin ?? false;

  const handleRemoveUser = async (userId: string, user: UserPermission) => {
    if (!appId) return;

    // Prevent removing yourself
    if (isCurrentUser(user)) {
      setSnackbar({
        open: true,
        message: 'You cannot remove yourself from the workspace',
        severity: 'error',
      });
      return;
    }

    // Show confirmation dialog
    setUserToRemove({ userId, user });
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove || !appId) return;

    setRemovingUserId(userToRemove.userId);
    try {
      await removeUser({
        tenantId,
        appId,
        userId: Number(userToRemove.userId),
      }).unwrap();

      setSnackbar({
        open: true,
        message: 'User removed successfully',
        severity: 'success',
      });
    } catch (_err) {
      setSnackbar({
        open: true,
        message: 'Failed to remove user',
        severity: 'error',
      });
    } finally {
      setRemovingUserId(null);
      setUserToRemove(null);
    }
  };

  const handleToggleAdmin = async (userId: string, user: UserPermission, isAdmin: boolean) => {
    if (!appId) return;

    // Prevent changing your own admin role
    if (isCurrentUser(user)) {
      setSnackbar({
        open: true,
        message: 'You cannot change your own admin role',
        severity: 'error',
      });
      return;
    }

    setUpdatingRoleUserId(userId);
    try {
      await updateUserAdmin({
        tenantId,
        appId,
        userId: Number(userId),
        isAdmin,
      }).unwrap();

      setSnackbar({
        open: true,
        message: `User role updated to ${isAdmin ? 'admin' : 'user'}`,
        severity: 'success',
      });
    } catch (_err) {
      setSnackbar({
        open: true,
        message: 'Failed to update user role',
        severity: 'error',
      });
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (error) {
    return (
      <PageContainer>
        <Alert severity="error">Failed to load team members.</Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeading
        title="Team"
        subtitle="Manage workspace members and their access roles"
        action={
          !isLoading ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowInviteDialog(true)}
              sx={{
                bgcolor: '#337ab7',
                '&:hover': { bgcolor: '#286090' },
                textTransform: 'none',
                fontSize: '14px',
                padding: '6px 12px',
                borderRadius: '4px',
              }}
            >
              Invite Members
            </Button>
          ) : undefined
        }
      />

      {/* Table - matches dataProtect exact styling */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <TableContainer
            sx={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Table
              sx={{
                borderCollapse: 'collapse',
                '& th, & td': {
                  borderRight: '1px solid #ddd',
                  '&:last-child': {
                    borderRight: 0,
                  },
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: '#f5f5f5',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333',
                      padding: '12px 8px',
                    }}
                  >
                    Members
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333',
                      padding: '12px 8px',
                      width: '150px',
                    }}
                  >
                    Role
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: '#333',
                      padding: '12px 8px',
                      width: '120px',
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedUsers && sortedUsers.length > 0 ? (
                  sortedUsers.map((user, index) => {
                    // Handle multiple possible data structures from API
                    const userWithIdentity = user as {
                      username?: string;
                      email?: string;
                      identity?: { username?: string };
                      Identity?: { Username?: string };
                    };
                    const displayName =
                      userWithIdentity.username ||
                      userWithIdentity.email ||
                      userWithIdentity.identity?.username ||
                      userWithIdentity.Identity?.Username ||
                      'Unknown';
                    const userId = String(user.userId || `user-${index}`);
                    const isRemoving = removingUserId === userId;
                    const isUpdatingRole = updatingRoleUserId === userId;
                    const isSelf = isCurrentUser(user);

                    return (
                      <TableRow
                        key={userId}
                        sx={{
                          bgcolor: 'white',
                          borderBottom: '1px solid #ddd',
                          '&:last-child': {
                            borderBottom: 0,
                          },
                          '&:hover': {
                            bgcolor: '#fafafa',
                          },
                        }}
                      >
                        {/* Members column */}
                        <TableCell
                          sx={{
                            fontSize: '14px',
                            color: '#333',
                            padding: '12px 8px',
                          }}
                        >
                          {displayName}
                          {isSelf && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                ml: 1,
                                color: 'text.secondary',
                                fontStyle: 'italic',
                              }}
                            >
                              (you)
                            </Typography>
                          )}
                        </TableCell>

                        {/* Role column with toggle - matches dataProtect exactly */}
                        <TableCell align="center" sx={{ padding: '12px 8px' }}>
                          <Tooltip
                            title={getRoleTooltipText(isSelf, isUpdatingRole, isCurrentUserAdmin)}
                            disableHoverListener={isCurrentUserAdmin && !isSelf && !isUpdatingRole}
                          >
                            <Box
                              sx={{
                                position: 'relative',
                                display: 'inline-flex',
                                alignItems: 'center',
                                width: 80,
                                height: 28,
                                opacity: !isCurrentUserAdmin || isSelf || isUpdatingRole ? 0.6 : 1,
                                cursor:
                                  !isCurrentUserAdmin || isSelf || isUpdatingRole
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              <Switch
                                checked={user.isAdmin}
                                onChange={(e) => handleToggleAdmin(userId, user, e.target.checked)}
                                disabled={!isCurrentUserAdmin || isSelf || isUpdatingRole}
                                size="small"
                                sx={{
                                  width: 80,
                                  height: 28,
                                  padding: 0,
                                  '& .MuiSwitch-switchBase': {
                                    padding: 0,
                                    margin: '2px',
                                    '&.Mui-checked': {
                                      transform: 'translateX(52px)',
                                      color: '#fff',
                                      '& + .MuiSwitch-track': {
                                        bgcolor: '#6772E5',
                                        opacity: 1,
                                        border: 0,
                                      },
                                    },
                                    '&.Mui-disabled': {
                                      color: '#fff',
                                      opacity: 0.7,
                                      '& + .MuiSwitch-track': {
                                        opacity: 0.7,
                                      },
                                    },
                                  },
                                  '& .MuiSwitch-thumb': {
                                    width: 24,
                                    height: 24,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  },
                                  '& .MuiSwitch-track': {
                                    borderRadius: 14,
                                    bgcolor: '#505050',
                                    opacity: 1,
                                    border: 0,
                                  },
                                }}
                              />
                              {isUpdatingRole ? (
                                <CircularProgress
                                  size={16}
                                  sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    position: 'absolute',
                                    left: user.isAdmin ? 10 : 44,
                                    color: 'white',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    textTransform: 'lowercase',
                                  }}
                                >
                                  {user.isAdmin ? 'admin' : 'user'}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        {/* Action column - matches dataProtect button styling */}
                        {/* Only admins can remove users */}
                        <TableCell align="center" sx={{ padding: '12px 8px' }}>
                          {isCurrentUserAdmin ? (
                            <Tooltip
                              title={isSelf ? 'You cannot remove yourself' : ''}
                              disableHoverListener={!isSelf}
                            >
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleRemoveUser(userId, user)}
                                  disabled={isRemoving || isSelf}
                                  sx={{
                                    bgcolor: '#337ab7',
                                    '&:hover': { bgcolor: '#286090' },
                                    '&:disabled': {
                                      bgcolor: '#ccc',
                                      color: '#666',
                                    },
                                    height: 28,
                                    minWidth: 70,
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    borderRadius: '4px',
                                    padding: '4px 12px',
                                  }}
                                >
                                  {isRemoving ? (
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                  ) : (
                                    'Remove'
                                  )}
                                </Button>
                              </span>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.disabled', fontSize: '13px' }}
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ padding: '24px' }}>
                      <Typography variant="body2" color="text.secondary">
                        No team members yet. Invite someone to collaborate.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!userToRemove}
        onClose={() => setUserToRemove(null)}
        onConfirm={confirmRemoveUser}
        title="Remove User"
        message={`Are you sure you want to remove "${
          userToRemove?.user?.username ||
          userToRemove?.user?.email ||
          // userToRemove?.user?.identity?.username ||
          'this user'
        }" from the workspace?`}
        confirmText="Remove"
        isDeleting={!!removingUserId}
      />

      {/* Invite Members Dialog */}
      {showInviteDialog && users && (
        <InviteDialog
          appId={appId!}
          tenantId={tenantId}
          currentWorkspaceUsers={users}
          isCurrentUserAdmin={isCurrentUserAdmin}
          onClose={() => setShowInviteDialog(false)}
          onSuccess={() => {
            setSnackbar({
              open: true,
              message: 'Member(s) invited successfully',
              severity: 'success',
            });
            setShowInviteDialog(false);
          }}
        />
      )}
    </PageContainer>
  );
};

/**
 * Invite Members Dialog
 * Allows inviting users by email OR selecting from organization members
 * Matches dataProtect userInviteModal.js design exactly
 */
interface InviteDialogProps {
  appId: string;
  tenantId: string;
  currentWorkspaceUsers: UserPermission[];
  isCurrentUserAdmin: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteDialog = ({
  appId,
  tenantId,
  currentWorkspaceUsers,
  isCurrentUserAdmin,
  onClose,
  onSuccess,
}: InviteDialogProps) => {
  const [emailInput, setEmailInput] = useState('');
  const [inviteList, setInviteList] = useState<
    Array<{ email?: string; userId?: number; username?: string; isAdmin: boolean }>
  >([]);
  const [error, setError] = useState('');

  const { data: allUsers = [] } = useGetUsersQuery();
  const [inviteUsers, { isLoading }] = useInviteUsersToProjectMutation();

  // Email regex validation - stricter, more realistic
  // Allows: letters, numbers, . _ % + - before @
  // Requires valid domain with TLD (at least 2 letters)
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Filter users from this tenant who are not already in the workspace
  const organizationUsers = allUsers
    .filter((u) => u.tenantid === tenantId)
    .filter(
      (u) =>
        !currentWorkspaceUsers.some((wu) => String(wu.userId) === String(u.id)) &&
        !inviteList.some((inv) => inv.userId === u.id)
    )
    .sort((a, b) => (a.username || '').localeCompare(b.username || ''));

  // Real-time validation as user types
  const getEmailValidationError = (): string => {
    const trimmedEmail = emailInput.trim().toLowerCase();

    if (!trimmedEmail) {
      return '';
    }

    // Check if user is already a workspace member
    if (
      currentWorkspaceUsers.some(
        (wu) =>
          wu.email?.toLowerCase() === trimmedEmail || wu.username?.toLowerCase() === trimmedEmail
      )
    ) {
      return 'The user is already a member of this Project';
    }

    // Check if already in invite list
    if (inviteList.some((item) => item.email === trimmedEmail)) {
      return 'You have already added this email to the invite list';
    }

    // Check if this email belongs to an org user (this is OK - will be added by ID)
    const existingOrgUser = organizationUsers.find(
      (u) => u.username?.toLowerCase() === trimmedEmail
    );
    if (existingOrgUser) {
      return ''; // Valid - existing org user
    }

    // Validate email format for new emails
    if (!emailRegex.test(trimmedEmail)) {
      return 'Please enter a valid email address';
    }

    return '';
  };

  const realTimeError = getEmailValidationError();

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim().toLowerCase();

    // Validation
    if (!trimmedEmail) {
      setError('Please enter an email address');
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (inviteList.some((item) => item.email === trimmedEmail)) {
      setError('You have already added this email to the invite list');
      return;
    }

    // Check if user is already a workspace member
    if (
      currentWorkspaceUsers.some(
        (wu) =>
          wu.email?.toLowerCase() === trimmedEmail || wu.username?.toLowerCase() === trimmedEmail
      )
    ) {
      setError('The user is already a member of this Project');
      return;
    }

    // Check if this email belongs to an org user
    const existingOrgUser = organizationUsers.find(
      (u) => u.username?.toLowerCase() === trimmedEmail
    );
    if (existingOrgUser) {
      // Add by userId instead of email
      setInviteList([
        ...inviteList,
        { userId: existingOrgUser.id, username: existingOrgUser.username, isAdmin: false },
      ]);
      setEmailInput('');
      setError('');
      return;
    }

    // Add new email invite
    setInviteList([...inviteList, { email: trimmedEmail, isAdmin: false }]);
    setEmailInput('');
    setError('');
  };

  const handleAddOrgUser = (userId: number, username: string) => {
    setInviteList([...inviteList, { userId, username, isAdmin: false }]);
  };

  const handleRemove = (index: number) => {
    setInviteList(inviteList.filter((_, i) => i !== index));
  };

  const handleToggleAdmin = (index: number) => {
    setInviteList(
      inviteList.map((item, i) => (i === index ? { ...item, isAdmin: !item.isAdmin } : item))
    );
  };

  const handleInvite = async () => {
    if (inviteList.length === 0) return;

    try {
      // Prepare the invite data array
      const inviteData = inviteList.map((invite) =>
        invite.userId
          ? { userId: invite.userId, isAdmin: invite.isAdmin }
          : { email: invite.email!, isAdmin: invite.isAdmin }
      );

      // Send all invites in one request
      await inviteUsers({
        tenantId,
        appId,
        users: inviteData,
      }).unwrap();

      onSuccess();
    } catch (_err) {
      setError('Failed to invite users. Please try again.');
    }
  };

  return (
    <Dialog
      open
      onClose={(_event, reason) => {
        // Only close when button is clicked, not on backdrop click or escape
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      slotProps={{
        paper: {
          sx: {
            height: 525,
            maxHeight: 525,
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, fontSize: '18px', fontWeight: 500 }}>
        Invite users to this Project
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 3 }}>
        <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
          {/* Invite List */}
          {inviteList.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  About access roles:
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, fontSize: '12px' }}>
                  <strong>Admin:</strong> Admin have the highest level of access to an
                  Organization/Project.
                  <br />
                  ie. Admin can <strong>view/invite/edit/remove</strong> other users having any of
                  the access role.
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  <strong>User:</strong> User have the lowest level of access to an
                  Organization/Project.
                  <br />
                  ie. User can only <strong>view</strong> other users and <strong>invite</strong>{' '}
                  others with user access role only.
                </Typography>
              </Alert>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Users to invite:
              </Typography>

              {inviteList.map((item, index) => (
                <Box
                  key={item.email || item.username || index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    p: 1,
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    bgcolor: 'white',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(index)}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: '#d32f2f',
                      color: 'white',
                      '&:hover': { bgcolor: '#b71c1c' },
                      fontSize: '18px',
                      fontWeight: 'bold',
                      mr: 1.5,
                    }}
                  >
                    −
                  </IconButton>

                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {item.email || item.username}
                  </Typography>

                  {/* Non-admins can only invite with "user" role */}
                  <Tooltip
                    title={!isCurrentUserAdmin ? 'Only admins can assign admin role' : ''}
                    disableHoverListener={isCurrentUserAdmin}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        width: 80,
                        height: 28,
                        opacity: !isCurrentUserAdmin ? 0.6 : 1,
                        cursor: !isCurrentUserAdmin ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Switch
                        checked={item.isAdmin}
                        onChange={() => handleToggleAdmin(index)}
                        disabled={!isCurrentUserAdmin}
                        size="small"
                        sx={{
                          width: 80,
                          height: 28,
                          padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: '2px',
                            '&.Mui-checked': {
                              transform: 'translateX(52px)',
                              color: '#fff',
                              '& + .MuiSwitch-track': {
                                bgcolor: '#6772E5',
                                opacity: 1,
                                border: 0,
                              },
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            width: 24,
                            height: 24,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          },
                          '& .MuiSwitch-track': {
                            borderRadius: 14,
                            bgcolor: '#505050',
                            opacity: 1,
                            border: 0,
                          },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: item.isAdmin ? 10 : 44,
                          color: 'white',
                          fontSize: '11px',
                          fontWeight: 600,
                          pointerEvents: 'none',
                          userSelect: 'none',
                          textTransform: 'lowercase',
                        }}
                      >
                        {item.isAdmin ? 'admin' : 'user'}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}

          {/* Email Input */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Invite new members by Email:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                type="email"
                placeholder="Enter email address"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value.replace(/ /g, '').toLowerCase());
                  setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
                error={!!realTimeError}
                helperText={realTimeError || ' '}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                  },
                }}
                slotProps={{
                  formHelperText: {
                    sx: { minHeight: '20px', margin: '4px 0 0 0' },
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleAddEmail}
                disabled={!emailInput.trim() || !!realTimeError}
                size="small"
                sx={{
                  minWidth: '70px',
                  bgcolor: '#337ab7',
                  '&:hover': { bgcolor: '#286090' },
                  height: 36,
                }}
              >
                Add
              </Button>
            </Box>
            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', mt: 0.5, fontSize: '11px' }}
              >
                {error}
              </Typography>
            )}
          </Box>

          {/* Organization Members */}
          {organizationUsers.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Invite members from this Organization:
              </Typography>
              <Box
                sx={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  bgcolor: '#fafafa',
                }}
              >
                {organizationUsers.map((user) => (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      borderBottom: '1px solid #eee',
                      '&:last-child': { borderBottom: 0 },
                      '&:hover': { bgcolor: '#f0f0f0', cursor: 'pointer' },
                      transition: 'background-color 0.2s',
                    }}
                    onClick={() => handleAddOrgUser(user.id, user.username)}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: '#5cb85c',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        mr: 1.5,
                        cursor: 'pointer',
                      }}
                    >
                      +
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '14px' }}>
                      {user.username}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #ddd', px: 3, py: 2 }}>
        <Button variant="outlined" color="error" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleInvite}
          disabled={inviteList.length === 0 || isLoading}
          sx={{
            minWidth: '70px',
            bgcolor: '#337ab7',
            '&:hover': { bgcolor: '#286090' },
          }}
        >
          {isLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
